import * as _ from 'lodash';
import { Component } from 'react';
import { Button } from 'react-bootstrap';

import Icon, { icons } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import TooltipIcon from '../../../generic/TooltipIcon';
import { createComparator } from '../helper';
import type { APIError, IAsyncStatus, ProjectGroupReleaseProject, Revision } from '../../../../types';
import type { SortOptions } from '../helper';

import ProjectReleaseRow from './ProjectReleaseRow';
import SortingHeaderItem from './SortingHeader';

const PAGE_SIZE = 10;

type ProjectTableProps = {
  releaseProjects: ProjectGroupReleaseProject[];
  projectsStatus: IAsyncStatus<APIError>;
  sortableBranchCommit?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  editMode?: boolean;
  createMode?: boolean;
  onProjectSelected?: (project: ProjectGroupReleaseProject) => void;
  onUpdateProject?: (project: ProjectGroupReleaseProject) => void;
};

type ProjectTableState = {
  sort: SortOptions;
  page: number;
};

class ProjectGroupReleaseProjectsTable extends Component<ProjectTableProps, ProjectTableState> {
  state = {
    page: 0,
    sort: { key: 'title', asc: true },
  };

  componentDidUpdate(prevProps: ProjectTableProps) {
    if (prevProps.releaseProjects.length > this.props.releaseProjects.length) {
      if (this.state.page > 0 && (this.props.releaseProjects.length / PAGE_SIZE) <= this.state.page) {
        // This rule is to prevent runaway rerenders, we're being careful here.
        this.setState(prevState => ({ page: prevState.page - 1 }));
      }
    }
  }

  onSortChange = (key: string, asc: boolean) => {
    this.setState({ sort: { key, asc } });
  };

  onProjectSelected = (project: ProjectGroupReleaseProject) => () => {
    if (this.props.onProjectSelected) {
      this.props.onProjectSelected(project);
    }
  };

  onChangeBranch = (project: ProjectGroupReleaseProject) => (branch: string) => {
    const { onUpdateProject } = this.props;
    if (!onUpdateProject) {
      return;
    }
    onUpdateProject({
      ...project,
      // unset the revision so that the dropdown will reload
      revision: undefined,
      branch,
    });
  };

  onChangeRevision = (project: ProjectGroupReleaseProject) => (revision: Revision) => {
    const { onUpdateProject } = this.props;
    if (!onUpdateProject) {
      return;
    }
    onUpdateProject({
      ...project,
      revision,
    });
  };

  onPageUp = () => {
    const maxPage = Math.floor(this.props.releaseProjects.length / PAGE_SIZE);
    if (this.state.page < maxPage) {
      this.setState((state => ({ page: state.page + 1 })));
    }
  };

  onPageDown = () => {
    if (this.state.page > 0) {
      this.setState((state => ({ page: state.page - 1 })));
    }
  };

  getProjectValue = (project: ProjectGroupReleaseProject, key: string) => {
    switch (key) {
      case 'revision':
        return project.revision?.loc?.revision ?? '';
      case 'title':
        return project.project.title;
      case 'updatedAt':
        return project.project.updatedAt;
      case 'lastAnalyzed':
        return project.revision?.dependency_cache_updated ?? project.revision?.updatedAt;
      default:
        return _.get(project, key);
    }
  };

  displayedProjectIndexes = (): [number, number] => {
    const { releaseProjects } = this.props;
    if (!releaseProjects) {
      return [0, 0];
    }
    if (releaseProjects.length < PAGE_SIZE) {
      return [0, releaseProjects.length - 1];
    }

    const page = this.state.page;
    const min = page * PAGE_SIZE;
    let max = (page + 1) * PAGE_SIZE;
    if (max >= releaseProjects.length) {
      max = releaseProjects.length;
    }

    return [min, max - 1];
  };

  render() {
    if (this.props.projectsStatus.loading) {
      return <Loader />;
    }
    const { releaseProjects: allProjects, sortableBranchCommit } = this.props;
    const [indexMin, indexMax] = this.displayedProjectIndexes();
    const projectComparator = createComparator(this.state.sort, this.getProjectValue);
    const projects = this.props.releaseProjects.slice().sort(projectComparator).slice(indexMin, indexMax + 1);
    const shouldShowRemoveButton = !(this.props.readOnly || (this.props.editMode && projects.length === 1));
    const branchTooltip = (
      <TooltipIcon
        id="project-group-release-projects-table-branch-tooltip"
        icon={icons.questionCircle}
        tooltip="Don't see the branch you're looking for? Make sure it's being tracked within the underlying project."
        placement="bottom"
      />
    );
    return (
      <div className="project-table">
        <div className="panel panel-select">
          <div className="panel-body nopadding">
            <table className="table nomargin-v table-fill">
              <thead>
                {
                  projects.length <= 0 ? (
                    <div className="empty-release-message"><em>This release contains no projects.</em></div>
                  ) : (
                    <tr>
                      <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="title">
                        Title
                      </SortingHeaderItem>
                      {sortableBranchCommit ? (
                        <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="branch">
                          Branch
                        </SortingHeaderItem>
                      ) : (<th className="margin-left-sm">Branch {branchTooltip}</th>)}
                      {sortableBranchCommit ? (
                        <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="revision">
                          Revision
                        </SortingHeaderItem>
                      ) : (<th>Revision</th>)}
                      <SortingHeaderItem onClick={this.onSortChange} currentSort={this.state.sort} sortKey="lastAnalyzed">
                        Last Analyzed
                      </SortingHeaderItem>
                      { !this.props.readOnly && (<th style={{ width: '5%' }}>{' '}</th>) }
                    </tr>
                  )
                }
              </thead>
              <tbody className={this.props.loading ? 'loading loading-cruncher' : ''}>
                { projects.map((project) => (
                  <ProjectReleaseRow
                    showRemoveButton={shouldShowRemoveButton}
                    key={`row-${project.project.locator}`}
                    onSelect={this.onProjectSelected(project)}
                    onChangeBranch={this.onChangeBranch(project)}
                    onChangeRevision={this.onChangeRevision(project)}
                    editMode={this.props.createMode || this.props.editMode}
                    project={project}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {
            allProjects.length > PAGE_SIZE && (
              <div className="table-footer">
                <div className="pagination">
                  <Button disabled={indexMin <= 0} onClick={this.onPageDown}>
                    <Icon icon={icons.angleLeft} />
                  </Button>
                  <span><b>{indexMin + 1}</b> - <b>{indexMax + 1}</b> of <b>{allProjects.length}</b></span>
                  <Button disabled={(indexMax + 1) === allProjects.length} onClick={this.onPageUp}>
                    <Icon icon={icons.angleRight} />
                  </Button>
                </div>
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

export default ProjectGroupReleaseProjectsTable;
